"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import { ChevronDown } from "lucide-react";
import type { FacetBucket } from "modules/collections/details/model/collection";
import type { GameId } from "modules/common/model/models";
import { GameIcon } from "modules/common/components/Icons/GameIcon";
import { FacetDistribution } from "./FacetDistribution";

interface FacetRangeInputProps {
  title: string;
  buckets: FacetBucket[];
  min: number;
  max: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  step?: number;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  gameExclusive?: GameId;
  currency?: string;
  onClear?: () => void;
}

export function FacetRangeInput({
  title,
  buckets,
  min,
  max,
  onMinChange,
  onMaxChange,
  step = 1,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  onToggleOpen,
  gameExclusive,
  currency,
  onClear,
}: FacetRangeInputProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledIsOpen ?? internalOpen;
  const handleToggle = onToggleOpen ?? (() => setInternalOpen((o) => !o));

  const { lowerBound, upperBound } = useMemo(() => {
    const values = (buckets ?? [])
      .map((b) => Number(b.value))
      .filter(Number.isFinite);
    return {
      lowerBound: values.length ? Math.floor(Math.min(...values)) : 0,
      upperBound: values.length ? Math.ceil(Math.max(...values)) : 20,
    };
  }, [buckets]);

  const [localMin, setLocalMin] = useState(min || lowerBound);
  const [localMax, setLocalMax] = useState(max || upperBound);

  useEffect(() => {
    setLocalMin(min || lowerBound);
  }, [min, lowerBound]);
  useEffect(() => {
    setLocalMax(max || upperBound);
  }, [max, upperBound]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMinChange = useCallback(
    debounce((value: number) => onMinChange(value), 500),
    [onMinChange]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMaxChange = useCallback(
    debounce((value: number) => onMaxChange(value), 500),
    [onMaxChange]
  );

  const handleLowChange = (e: { target: { value: string } }) => {
    const val = Number(e.target.value);
    const clamped = Math.min(val, localMax - step);
    setLocalMin(clamped);
    debouncedMinChange(clamped <= lowerBound ? 0 : clamped);
  };

  const handleHighChange = (e: { target: { value: string } }) => {
    const val = Number(e.target.value);
    const clamped = Math.max(val, localMin + step);
    setLocalMax(clamped);
    debouncedMaxChange(clamped >= upperBound ? 0 : clamped);
  };

  const range = upperBound - lowerBound || 1;
  const leftPercent = ((localMin - lowerBound) / range) * 100;
  const rightPercent = ((upperBound - localMax) / range) * 100;

  const currencySymbol =
    currency === "USD"
      ? "$"
      : currency === "EUR"
        ? "\u20AC"
        : currency
          ? `${currency} `
          : "";
  const fmt = (v: number) => {
    const num = Number.isInteger(v) ? String(v) : v.toFixed(2);
    return currencySymbol ? `${currencySymbol}${num}` : num;
  };

  const hasActive = min > 0 || max > 0;

  return (
    <div className="border-base-content/10 border-b last:border-b-0">
      <div className="hover:bg-base-300 flex items-center transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm font-medium"
          onClick={handleToggle}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          />
          <span className="flex-1 text-left">{title}</span>
          {gameExclusive && (
            <span className="text-base-content/40 flex items-center">
              <GameIcon game={gameExclusive} />
            </span>
          )}
          {hasActive && (
            <span className="badge badge-primary badge-xs">
              {fmt(min || lowerBound)}–{fmt(max || upperBound)}
            </span>
          )}
        </button>
        {hasActive && onClear && (
          <button
            type="button"
            className="text-base-content/50 hover:text-error pr-2 text-xs transition-colors"
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-3 pb-3 pt-1">
            <FacetDistribution
              buckets={buckets}
              lowerBound={lowerBound}
              upperBound={upperBound}
              activeMin={localMin}
              activeMax={localMax}
            />

            {/* Slider track */}
            <div className="relative h-6">
              {/* Background track */}
              <div className="bg-base-300 absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full" />

              {/* Active range highlight */}
              <div
                className="bg-primary absolute top-1/2 h-1 -translate-y-1/2 rounded-full"
                style={{ left: `${leftPercent}%`, right: `${rightPercent}%` }}
              />

              {/* Low thumb */}
              <input
                type="range"
                min={lowerBound}
                max={upperBound}
                step={step}
                value={localMin}
                onChange={handleLowChange}
                className="[&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:bg-primary pointer-events-none absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full"
              />

              {/* High thumb */}
              <input
                type="range"
                min={lowerBound}
                max={upperBound}
                step={step}
                value={localMax}
                onChange={handleHighChange}
                className="[&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:bg-primary pointer-events-none absolute inset-0 z-20 m-0 h-full w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full"
              />
            </div>

            {/* Labels */}
            <div className="text-base-content/50 flex justify-between text-xs">
              <span>{fmt(localMin)}</span>
              <span>{fmt(localMax)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
